import React, { useEffect } from "react";
import { useFetchedWithPendingReference, useStale } from "../hooks";
import { GenericNotFoundPageLayout } from "../layouts/generic-not-found/GenericNotFoundPageLayout";
import { useNavigate, useLocation } from "react-router";
import { ModalLoadingSpinner } from "./modal-loading-spinner";
import { storefrontClient } from "../domain/storefront";
import { useScrollToTopLinkEffect } from "./use-scroll-to-top-link";
import { usePageViewTracking } from "./use-page-view-tracking";
import { fallbackNotFoundTranslations } from "./fallback-route";
import { PublicConfigurationT } from "@swo/shared";
import { useRiskifiedIntegration } from "./use-riskified-integration";
import { useLastLocaleSegmentPair } from "./use-last-locale-segment-pair";
import { readInitialPageDataForPath } from "./read-initial-page-data-for-path";
import { AppView } from "../styles/AppView";
import { getStalenessAcceptance } from "../utils/react/get-staleness-acceptance";
import { LoadedStorefrontPage } from "./LoadedPage";

export function StorefrontPages(props: { config: PublicConfigurationT }) {
  const mode = props.config.preview ? "preview" : "published";
  const intercom = props.config.integrations.intercom;
  const riskifiedStoreDomain = props.config.riskified.storeDomain;

  const navigate = useNavigate();
  // undefined | { scrollToTop?: boolean; expectSimilarLayout?: boolean; recoverCartId?: string }
  const location = useLocation();

  const path = location.pathname;

  const [maybePage, requestRef] = useFetchedWithPendingReference(() => storefrontClient.getPage(path), {
    dataKey: path,
    initialState: readInitialPageDataForPath(path),
  });

  const scrollToTop = !!location.state?.scrollToTop;

  const expectSimilarLayout = !!location.state?.expectSimilarLayout;

  // The idea behind this hook is to slightly lag the UI behind instead of showing an empty "loading" state
  // small delay is not perceived by humans as slowness and allows to reduce huge layout shift.

  // This is when a link is clicked with scroll to top set to true
  // having a stale state here is more weird than having a blank page...
  // so let's use blank page!
  const stalenessAcceptanceMs = getStalenessAcceptance(path, scrollToTop, expectSimilarLayout);
  const maybePageWithStaleness = useStale(maybePage, stalenessAcceptanceMs, t => t.data.__tag === "loaded");

  // This needs to happen after staleness check - otherwise scrollToTop is overridden

  useScrollToTopLinkEffect(maybePage, location.state?.scrollToTop);

  useEffect(() => {
    if (
      maybePage.data.__tag === "loaded" &&
      maybePageWithStaleness.data.__tag === "loaded" &&
      expectSimilarLayout &&
      requestRef.current === null
    ) {
      void navigate(location, {
        replace: true,

        state: {
          ...location.state,
          expectSimilarLayout: undefined,
        },
      });
    }
  }, [maybePage, maybePageWithStaleness, expectSimilarLayout]);
  const lastLocaleSegmentPair = useLastLocaleSegmentPair(maybePage);

  usePageViewTracking(maybePage);

  useRiskifiedIntegration(
    {
      storeDomain: riskifiedStoreDomain,
    },
    location.pathname,
    lastLocaleSegmentPair?.locale.shopMode.cart !== "enabled",
  );

  switch (maybePageWithStaleness.data.__tag) {
    case "loading": {
      return <ModalLoadingSpinner />;
    }
    case "loaded": {
      const pageData = maybePageWithStaleness.data.data;

      return (
        <LoadedStorefrontPage
          intercom={intercom}
          mode={mode}
          pageData={pageData}
          recoverCartId={new URLSearchParams(location.search).get("recoverCartId")}
          showLoadingIndicator={maybePage.data.__tag === "loading"}
          config={props.config}
        />
      );
    }
    case "error": {
      return (
        <AppView>
          <GenericNotFoundPageLayout translations={fallbackNotFoundTranslations} />
        </AppView>
      );
    }
  }
}
